import { NavLink, useNavigate } from 'react-router-dom';
import {
  BarChart3, Package, MessageCircle, Bell, MapPin,
  ShoppingBasket, Settings, LogOut, ChevronDown, MoreHorizontal, ShoppingCart,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import sisthokoLogo from '@/assets/sisthoko-logo-white.png';
import { BetaBadge } from '@/components/BetaBadge';
import { NotificationsBell } from '@/components/NotificationsBell';
import { MasterCartIconButton } from '@/components/cart/MasterCart';

type Item = { title: string; url: string; icon: React.ElementType };

const primary: Item[] = [
  { title: 'Dashboard', url: '/dashboard', icon: BarChart3 },
  { title: 'My Baskets', url: '/baskets', icon: ShoppingBasket },
  { title: 'Orders', url: '/orders', icon: Package },
];

const account: Item[] = [
  { title: 'WhatsApp Settings', url: '/settings/whatsapp', icon: MessageCircle },
  { title: 'Phone Alerts', url: '/settings/notifications', icon: Bell },
  { title: 'My Location', url: '/settings/location', icon: MapPin },
  { title: 'Basket Preferences', url: '/settings/basket', icon: ShoppingCart },
  { title: 'Profile', url: '/settings/profile', icon: Settings },
];

export function AppTopNav() {
  const { profile } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const initials = (profile?.full_name || profile?.email || '?')
    .split(/[\s@.]+/).filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase()).join('');
  const displayName = (profile?.full_name || profile?.email || 'Account').split(' ')[0];

  return (
    <header className="hidden md:block sticky top-0 z-30 border-b border-sidebar-border/60 bg-sidebar text-sidebar-foreground">
      <div className="mx-auto max-w-screen-2xl px-5 h-16 flex items-center gap-5">
        {/* Brand lockup */}
        <NavLink to="/dashboard" className="flex items-center gap-3 shrink-0 group">
          <span className="h-12 w-12 rounded-2xl bg-gradient-to-br from-white/20 to-white/5 grid place-items-center ring-1 ring-white/20 shadow-sm group-hover:from-white/25 group-hover:to-white/10 transition">
            <img src={sisthokoLogo} alt="Sis Thoko" className="h-8 w-8 object-contain" />
          </span>
          <BetaBadge variant="inverted" />
        </NavLink>

        {/* Primary nav */}
        <nav className="flex items-center gap-1">
          {primary.map((item) => (
            <NavLink
              key={item.title}
              to={item.url}
              end
              className={({ isActive }) =>
                `inline-flex items-center gap-1.5 px-3.5 h-9 rounded-full text-sm font-semibold transition ${
                  isActive
                    ? 'bg-white text-sidebar shadow-sm'
                    : 'text-sidebar-foreground/75 hover:bg-white/10 hover:text-sidebar-foreground'
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              <span>{item.title}</span>
            </NavLink>
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-sm font-semibold text-sidebar-foreground/75 hover:bg-white/10 hover:text-sidebar-foreground transition">
                <MoreHorizontal className="h-4 w-4" />
                <span>More</span>
                <ChevronDown className="h-3.5 w-3.5 opacity-70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56 rounded-2xl">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Account
              </DropdownMenuLabel>
              {account.map((item) => (
                <DropdownMenuItem key={item.title} onClick={() => navigate(item.url)} className="cursor-pointer rounded-lg">
                  <item.icon className="h-4 w-4 mr-2" />
                  {item.title}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </nav>

        {/* Right cluster */}
        <div className="flex items-center gap-2 shrink-0 ml-auto">
          <span className="hidden xl:inline-flex items-center gap-1.5 text-[11px] font-medium text-sidebar-foreground/70 border border-white/15 rounded-full px-2.5 py-1">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Live
          </span>
          <div className="text-sidebar-foreground">
            <NotificationsBell />
          </div>
          <div className="text-sidebar-foreground">
            <MasterCartIconButton />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="inline-flex items-center gap-2 pl-1 pr-3 h-10 rounded-full bg-white/5 hover:bg-white/15 border border-white/10 transition">
                <span className="h-8 w-8 rounded-full bg-primary text-primary-foreground text-[12px] font-bold flex items-center justify-center">
                  {initials || 'U'}
                </span>
                <span className="hidden xl:inline text-xs font-semibold text-sidebar-foreground max-w-[100px] truncate">
                  {displayName}
                </span>
                <ChevronDown className="h-3.5 w-3.5 text-sidebar-foreground/70" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60 rounded-2xl">
              <DropdownMenuLabel className="truncate">
                <div className="font-semibold">{profile?.full_name || 'Account'}</div>
                <div className="text-[11px] font-normal text-muted-foreground truncate">{profile?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings/profile')} className="cursor-pointer rounded-lg">
                <Settings className="h-4 w-4 mr-2" /> Profile & Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="cursor-pointer rounded-lg text-destructive focus:text-destructive">
                <LogOut className="h-4 w-4 mr-2" /> Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
